import { Controller } from "@hotwired/stimulus"
import Cropper from 'cropperjs'

export default class extends Controller {
  static targets = ["canvas", "input", "modal", "image", "canvasImage", "form", "selection", "withinCanvas", "withinImage", "withinNone"]
  static values = { aspectRatio: { type: Number, default: 300 }, size: { type: Number, default: 300 } }

  connect() {
    this.cropper = null
    this.handleModalShown = this.handleModalShown.bind(this)
    this.setupModal()
    this.originalFile = ''
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener('shown.bs.modal', this.handleModalShown)
    }
  }

  setupModal() {
    if (!this.hasModalTarget) return

    this.modalTarget.removeEventListener('shown.bs.modal', this.handleModalShown)
    this.modalTarget.addEventListener('shown.bs.modal', this.handleModalShown)
  }

  handleModalShown() {
    if (this.hasInputTarget && this.inputTarget.files.length > 0) {
      this.loadImage(this.inputTarget.files[0])
    }
  }

  resetModal() {
    if (this.cropper?.getCropperCanvas()) {
      this.cropper.getCropperCanvas().remove()
    }

    this.cropper = null
    this.originalFile = ''

    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }

    if (this.hasImageTarget) {
      this.imageTarget.removeAttribute('src')
    }
  }

  formElement() {
    return this.element.closest('form')
  }

  cropDetailsField() {
    const form = this.formElement()
    if (!form) return null

    return form.querySelector('[data-menu-image-cropper-field="crop-details"]') || document.getElementById('menu_item_crop_details')
  }

  updateLegacyTriggerLabel() {
    const trigger = this.formElement()?.querySelector('.pick_image_identifier')

    if (!trigger || !this.originalFile?.name) return

    trigger.textContent = `Image Selected (${this.originalFile.name})`
  }

  notifySelectionSaved() {
    const form = this.formElement()
    if (!form) return

    form.dispatchEvent(new CustomEvent('menu-image-cropper:selection-saved', {
      bubbles: true,
      detail: {
        fileName: this.originalFile?.name || '',
      },
    }))
  }

  loadImage(event) {
    // Handle both direct file passing and event passing
    const file = event instanceof Event ? event.target.files[0] : event
    
    // Validate the file object
    if (!file || !(file instanceof Blob)) {
      console.error('Invalid file provided:', file)
      return
    }

    this.originalFile = file;


    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif']
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPEG, or GIF image')
      return
    }

    // Check file size (5MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be smaller than 5MB')
      return
    }

    const reader = new FileReader()
    
    reader.onload = (e) => {
      if (!this.hasImageTarget) {
        console.error("Image target not found")
        return
      }
      
      this.imageTarget.src = e.target.result
      this.initCropper()
    }
    
    reader.onerror = () => {
      console.error('Error reading file')
    }
    
    reader.readAsDataURL(file)
  }

  initCropper() {
    if (this.cropper) {
      this.cropper.getCropperCanvas().remove();
    }

    const DEFAULT_TEMPLATE = ('<cropper-canvas background style="min-height:300px;">'
        + '<cropper-image rotatable scalable skewable translatable></cropper-image>'
        + '<cropper-shade hidden></cropper-shade>'
        + '<cropper-handle action="select" plain></cropper-handle>'
        + '<cropper-selection initial-coverage="0.5" id="cropperSelection" initial-aspect-ratio="1" aspect-ratio="1" movable resizable>'
        + '<cropper-grid role="grid" bordered covered></cropper-grid>'
        + '<cropper-crosshair centered></cropper-crosshair>'
        + '<cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>'
        + '<cropper-handle action="n-resize"></cropper-handle>'
        + '<cropper-handle action="e-resize"></cropper-handle>'
        + '<cropper-handle action="s-resize"></cropper-handle>'
        + '<cropper-handle action="w-resize"></cropper-handle>'
        + '<cropper-handle action="ne-resize"></cropper-handle>'
        + '<cropper-handle action="nw-resize"></cropper-handle>'
        + '<cropper-handle action="se-resize"></cropper-handle>'
        + '<cropper-handle action="sw-resize"></cropper-handle>'
        + '</cropper-selection>'
        + '</cropper-canvas>');

    this.cropper = new Cropper(this.imageTarget, {
      template: DEFAULT_TEMPLATE,
      aspectRatio: "1",
      viewMode: 2, // Restrict everything outside crop box
      dragMode: 'move', // Only allow moving the image
      autoCrop: true,
      autoCropArea: 1,
      responsive: true,
      restore: false,
      guides: true,
      center: false,
      highlight: false,
      cropBoxMovable: true,
      cropBoxResizable: false, // Disable resizing completely
      toggleDragModeOnDblclick: false
    });

    this.cropper.getCropperSelection().addEventListener('change', this.onCropperSelectionChange.bind(this));
    this.cropper.getCropperImage().addEventListener('transform', this.onCropperImageTransform.bind(this));
  }

  inSelection(selection, maxSelection) {
    return (
      selection.x >= maxSelection.x &&
      selection.y >= maxSelection.y &&
      (selection.x + selection.width) <= (maxSelection.x + maxSelection.width) &&
      (selection.y + selection.height) <= (maxSelection.y + maxSelection.height)
    )
  }

  onCropperImageTransform(event) {
    if (!this.cropper.getCropperCanvas()) {
      return
    }

    const imageClone = this.cropper.getCropperImage().cloneNode(true)
    imageClone.style.transform = `matrix(${event.detail.matrix.join(', ')})`
    imageClone.style.opacity = '0'
    this.cropper.getCropperCanvas().appendChild(imageClone)

    const canvasRect = this.cropper.getCropperCanvas().getBoundingClientRect()
    const selectionRect = this.cropper.getCropperSelection().getBoundingClientRect()
    const imageRect = imageClone.getBoundingClientRect()
    this.cropper.getCropperCanvas().removeChild(imageClone)

    const selection = this.cropper.getCropperSelection();
    const maxSelection = {
      x: imageRect.left - canvasRect.left,
      y: imageRect.top - canvasRect.top,
      width: imageRect.width,
      height: imageRect.height
    }

    if (!this.inSelection(selection, maxSelection)) {
      event.preventDefault()
    }
  }

  onCropperSelectionChange(event) {
    if (!this.cropper.getCropperCanvas()) {
      return
    }

    const canvasRect = this.cropper.getCropperCanvas().getBoundingClientRect()
    const selection = event.detail
    const imageRect = this.cropper.getCropperImage().getBoundingClientRect()
    const maxSelection = {
      x: imageRect.left - canvasRect.left,
      y: imageRect.top - canvasRect.top,
      width: imageRect.width,
      height: imageRect.height
    }
    
    if (!this.inSelection(selection, maxSelection)) {
      event.preventDefault()
    }
  }

  saveImage() {
    if (!this.cropper || !this.originalFile) {
      return
    }

    // Get all relevant elements and measurements
    const canvas = this.cropper.getCropperCanvas();
    const image = this.cropper.getCropperImage();
    const selection = this.cropper.getCropperSelection();

    // Get DOM measurements
    const imageRect = image.getBoundingClientRect();
    const selectionRect = selection.getBoundingClientRect();

    // Calculate position relative to image (not canvas)
    const imageX = selectionRect.x - imageRect.x;
    const imageY = selectionRect.y - imageRect.y;

    // Get dimensions
    const displayedWidth = imageRect.width;
    const displayedHeight = imageRect.height;
    const naturalWidth = image.clientWidth;
    const naturalHeight = image.clientHeight;

    // Calculate scale factors
    const scaleX = naturalWidth / displayedWidth;
    const scaleY = naturalHeight / displayedHeight;

    // Calculate average scale to maintain aspect ratio
    const avgScale = (scaleX + scaleY) / 2;

    // Enforce square dimensions - use the smaller dimension
    const cropSize = Math.min(
      Math.round(selectionRect.width * avgScale),
      Math.round(selectionRect.height * avgScale)
    );

    // Scale to original image dimensions
    const scaledSelection = {
      x: Math.round(imageX * avgScale),
      y: Math.round(imageY * avgScale),
      width: cropSize,
      height: cropSize
    };

    // Validate coordinates
    if (scaledSelection.x < 0 || scaledSelection.y < 0 ||
        scaledSelection.x + scaledSelection.width > naturalWidth ||
        scaledSelection.y + scaledSelection.height > naturalHeight) {
      console.error('Crop area exceeds image boundaries');
      return
    }

    const logData = {
      selection: scaledSelection,
      file: {
        name: this.originalFile?.name || 'unknown',
        type: this.originalFile?.type || 'unknown',
      },
      timestamp: new Date().toISOString()
    };

    const cropDetailsField = this.cropDetailsField()
    if (cropDetailsField) {
      cropDetailsField.value = JSON.stringify(logData)
    }

    this.updateLegacyTriggerLabel()
    this.notifySelectionSaved()
  }
}
